import { CancelConfirmModal } from '@chiroup/components/CancelConfirmModal';
import { InlineToast } from '@chiroup/components/feedback';
import { ChiroUpAppointmentCommon } from '@chiroup/core/constants/stringConstants';
import { AppointmentStatuses } from '@chiroup/core/types/Appointment.type';
import { HealthComLocation } from '@chiroup/core/types/HealthComLocation.type';
import { ArrowDownTrayIcon, ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { CalendarIcon, TrashIcon } from '@heroicons/react/24/solid';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import useAppointments from '../../hooks/useAppointments';
import usePastAppointments from '../../hooks/usePastAppointments';
import { downloadAppointmentIcs } from '../../utils/buildAppointmentIcs';
import AppointmentsSkeleton from './AppointmentsSkeleton';
import ChangeAppointmentButton from './ChangeAppointmentButton';

type Props = {
  location?: HealthComLocation;
  path: string;
  // Fallback clinic identity for clinics without a vanity URL path — the
  // authed portal is keyed by clinicId, not path.
  clinicId?: number | null;
  primaryColor: string;
  me: { patients: any[] } | null;
};

// Renders a single appointment row. Hoisted out of the component because
// the same markup powers both the Upcoming and Past sections — keeping it
// inline would have meant repeating ~80 lines of JSX. Falls back to
// `displayValues.clinicianName` when the provider isn't found in
// `location.providers`, which can happen now that the patient sees
// appointments from any of the clinic's locations (not just the one tied
// to the URL path).
type RenderAppointmentArgs = {
  appointment: any;
  location: HealthComLocation;
  primaryColor: string;
  path: string;
  showPatientNames: boolean;
  relativeDayLabel: (d: dayjs.Dayjs, tz: string) => string;
  setCancelId: (id: string | null) => void;
  updateAppointment: (id: string, data: any) => void;
  onCalendarFail: () => void;
};

const renderAppointmentCard = ({
  appointment,
  location,
  primaryColor,
  path,
  showPatientNames,
  relativeDayLabel,
  setCancelId,
  updateAppointment,
  onCalendarFail,
}: RenderAppointmentArgs) => {
  const provider = location.providers?.find(p => p.ID === appointment.clinicianId);
  const providerName = provider?.name || appointment?.displayValues?.clinicianName || '';
  const treatmentName = appointment?.displayValues?.treatmentName || '';
  const patientName = showPatientNames ? appointment?.displayValues?.patientName : undefined;
  // Render in the appointment's location timezone (not the browser's).
  // Each appointment carries its own `tz` from the JOIN; falls back to
  // the path's location for legacy rows that lack it. Availability
  // slots in the rescheduling UI are clinic-tz, so without this the
  // same appointment would show different times across surfaces.
  const apptTz = appointment.tz || location.timezone;
  const startDayjs = dayjs(appointment.startTime as any).tz(apptTz);
  const isCanceled = appointment.status === AppointmentStatuses.Canceled;
  // Orphaned: the appointment time has passed but it's still in Scheduled
  // status (never checked in). For these we hide both Change and Add to
  // calendar — neither action makes sense after the slot is gone.
  const isOrphaned =
    appointment.status === AppointmentStatuses.Scheduled && startDayjs.isBefore(dayjs().tz(apptTz));
  const isChangeable =
    appointment.status === AppointmentStatuses.Scheduled &&
    !!appointment.clinicianId &&
    appointment.treatmentOnlineBooking !== false &&
    !isOrphaned;

  return (
    <div key={appointment.id} className="portal-card p-4 flex items-start gap-4">
      <div
        className="shrink-0 flex flex-col items-center justify-center rounded-2xl px-3 py-2 min-w-16"
        style={{
          backgroundColor: `${primaryColor}15`,
          color: primaryColor,
        }}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          {startDayjs.format('MMM')}
        </span>
        <span className="text-2xl font-bold tabular-nums leading-7">{startDayjs.format('D')}</span>
      </div>
      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
        <div className="flex items-start gap-2">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            {providerName}
          </p>
          {isCanceled && (
            <span className="shrink-0 rounded-md whitespace-nowrap px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset text-red-700 bg-red-50 ring-red-500/10 dark:bg-red-500/10 dark:ring-red-500/20 dark:text-red-300">
              Canceled
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {treatmentName}
          {patientName && (
            <>
              <span className="text-gray-300 dark:text-gray-600"> · </span>
              {patientName}
            </>
          )}
        </p>
        <p className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          {relativeDayLabel(startDayjs, apptTz)}
          <span className="text-gray-300 dark:text-gray-600 mx-1.5">·</span>
          {startDayjs.format('h:mm A')}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500">{appointment.duration} min</p>
        {!isCanceled && !isOrphaned && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {isChangeable && (
              <ChangeAppointmentButton
                primaryColor={primaryColor}
                location={location}
                id={appointment.id}
                provider={appointment.clinicianId}
                treatment={appointment.treatmentId}
                startTime={appointment.startTime}
                startTimeFormatted={appointment.startTimeFormatted}
                cancel={() => setCancelId(appointment.id)}
                updateAppointment={updateAppointment}
                path={appointment.locationPath || path}
              />
            )}
            <button
              type="button"
              onClick={async () => {
                const ok = await downloadAppointmentIcs({
                  appointmentId: appointment.id,
                  startTimestamp: appointment.startTime as any,
                  durationMinutes: appointment.duration,
                  providerName,
                  treatmentName,
                  patientName,
                  location,
                });
                if (!ok) onCalendarFail();
              }}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-200 dark:ring-white/10 text-gray-700 dark:text-gray-300 transition-colors active:bg-gray-50 dark:active:bg-white/5"
              aria-label="Add to calendar"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Add to calendar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Appointments: React.FC<Props> = ({ location, path, clinicId, primaryColor, me }) => {
  // Scope to the active patient — family accounts share an email/phone,
  // so without this every household member's appointments would land
  // on whoever happened to be selected.
  const { selectedPatient } = usePatientPortal();
  const patientId = selectedPatient?.ID || null;

  const { data, cancel, isCancelling, updateAppointment, isFetching } = useAppointments(
    path,
    patientId,
    clinicId
  );
  const {
    data: pastData,
    fetchNextPage: fetchNextPastPage,
    hasNextPage: hasNextPastPage,
    isFetchingNextPage: isFetchingNextPastPage,
    isFetching: isFetchingPast,
  } = usePastAppointments(path, patientId, clinicId);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [calendarToast, setCalendarToast] = useState(false);
  const calendarToastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(calendarToastTimer.current), []);
  const onCalendarFail = useCallback(() => {
    clearTimeout(calendarToastTimer.current);
    setCalendarToast(true);
    calendarToastTimer.current = setTimeout(() => setCalendarToast(false), 5000);
  }, []);

  const [error, setError] = useState({
    active: false,
    text: '',
    cancelText: 'Close',
  });

  // Upcoming appointments arrive sorted asc from the server. Past
  // appointments are server-paginated — flatten the infinite-query
  // pages into a single list for rendering.
  const upcoming = useMemo(
    () =>
      (Array.isArray(data) ? data : [])
        .slice()
        .sort((a, b) => Number(a.startTime) - Number(b.startTime)),
    [data]
  );
  const past = useMemo(() => pastData?.pages.flatMap(page => page.items) ?? [], [pastData]);

  if (!location) {
    return null;
  }

  // The list is already scoped to the selected patient via patientId,
  // so the per-card "For X" subtitle would be redundant. Only fall back
  // to showing it when the page is somehow rendered without a selection.
  const showPatientNames = !patientId && (me?.patients?.length ?? 0) !== 1;

  const relativeDayLabel = (d: dayjs.Dayjs, tz: string) => {
    // Compare against "today" in the appointment's tz so the
    // Today/Tomorrow/Yesterday labels respect the clinic's day boundary
    // rather than the browser's.
    const diff = d.startOf('day').diff(dayjs().tz(tz).startOf('day'), 'day');

    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === -1) return 'Yesterday';

    return d.format('ddd, MMM D, YYYY');
  };

  const header = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">My Appointments</h2>
        <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
          View and manage your visits.
        </p>
      </div>
      <Link
        to="/schedule"
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white transition-opacity active:opacity-80"
        style={{ backgroundColor: primaryColor }}
      >
        <PlusIcon className="h-4 w-4" />
        New
      </Link>
    </div>
  );

  // While the upcoming list is initializing, show the page-level loader.
  // Past pages can resolve later — the Past section just delays itself.
  const isInitialLoad = isFetching && !data;
  const hasNothing = !isFetching && !upcoming.length && !past.length && !isFetchingPast;

  return (
    <>
      {isInitialLoad ? (
        <AppointmentsSkeleton />
      ) : hasNothing ? (
        <div className="flex flex-col gap-5">
          {header}
          <div className="portal-card px-6 py-16 text-center">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
            <h3 className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">
              No appointments yet
            </h3>
            <p className="mt-1 text-sm text-gray-400 dark:text-gray-500 max-w-sm mx-auto">
              When you book an appointment, it&apos;ll show up here.
            </p>
            <div className="mt-6">
              <Link
                to="/schedule"
                className="inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-full text-white transition-opacity active:opacity-80"
                style={{ backgroundColor: primaryColor }}
              >
                Book an appointment
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {header}

          {upcoming.length > 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                Upcoming
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {upcoming.map(appointment =>
                  renderAppointmentCard({
                    appointment,
                    location,
                    primaryColor,
                    path,
                    showPatientNames,
                    relativeDayLabel,
                    setCancelId,
                    updateAppointment,
                    onCalendarFail,
                  })
                )}
              </div>
            </section>
          )}

          {past.length > 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                Past
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {past.map(appointment =>
                  renderAppointmentCard({
                    appointment,
                    location,
                    primaryColor,
                    path,
                    showPatientNames,
                    relativeDayLabel,
                    setCancelId,
                    updateAppointment,
                    onCalendarFail,
                  })
                )}
              </div>
              {hasNextPastPage && (
                <button
                  type="button"
                  onClick={() => fetchNextPastPage()}
                  disabled={isFetchingNextPastPage}
                  className="self-center mt-1 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-200 dark:ring-white/10 text-gray-700 dark:text-gray-300 transition-colors active:bg-gray-50 dark:active:bg-white/5 disabled:opacity-60"
                >
                  {isFetchingNextPastPage ? 'Loading…' : 'Show more'}
                </button>
              )}
            </section>
          )}
        </div>
      )}

      <InlineToast
        show={calendarToast}
        variant="error"
        icon={<ExclamationTriangleIcon className="h-4 w-4" />}
      >
        Unable to add to calendar. If you are using an in-app browser, try opening your browser
        instead.
      </InlineToast>

      <CancelConfirmModal
        isOpen={!!cancelId}
        title="Cancel appointment"
        confirmText="Cancel Appointment"
        description="Are you sure you want to cancel this appointment? You can reschedule at any time."
        close={() => {
          setError({ ...error, active: false, text: '' });
          setCancelId(null);
        }}
        setCancelModalOpen={() => setCancelId(null)}
        selectedAppointmentId={cancelId}
        saveAppointmentFromHealthComSchedule={async (id: string, reason: string) => {
          setError({ ...error, active: false, text: '' });

          try {
            await cancel(id, reason);
            setCancelId(null);
          } catch (e: any) {
            setError({
              ...error,
              active: true,
              text: e.response?.data?.message || ChiroUpAppointmentCommon.cancelFailed,
            });
          }
        }}
        error={error}
        primaryColor={primaryColor}
        noColor={!primaryColor}
        cancelText="Keep Appointment"
        icon={<TrashIcon className="h-6 w-6 text-gray-600 dark:text-dark-gray-400" />}
        loading={isCancelling}
      />
    </>
  );
};

export default Appointments;
